import axios from 'axios';
import { useEffect, useState } from 'react';
const baseURL = process.env.REACT_APP_BASE_URL;


interface WithdrawalItem {
  uid: string;
  transaction_id: number;
  transaction_date: string;
  name: string;
  amount: number;
  status: string;
  utr_no: string; // Added to match the new data structure
}

const ManualWithdrawal = () => {
  const [isManualPayment, setIsManualPayment] = useState(true); // To toggle between manual and online withdrawals
  const [withdrawalData, setWithdrawalData] = useState<WithdrawalItem[]>([]);

  useEffect(() => {
    fetchWithdrawalData();
  }, [isManualPayment]); // Re-fetch data when payment type changes

  // Fetch withdrawal data from the API based on transaction type
  const fetchWithdrawalData = async () => {
    try {
      const transactionType = isManualPayment ? 'manual' : 'online'; // Set transaction type based on state
      const response = await axios.get(`${baseURL}/getManualWithdrawalData`, {
        params: { transactionType }, 
        headers:{
          "Content-Type": "application/json",
          "authorization":localStorage.getItem("token") || ""
        }
      });
      const data = response.data;

      if (Array.isArray(data)) {
        const reversedData = data.reverse(); // Optionally reverse data for most recent first
        setWithdrawalData(reversedData);
      } else {
        console.error('Unexpected response structure:', data);
        setWithdrawalData([]);
      }
    } catch (error) {
      console.error('Failed to fetch withdrawal data:', error);
      setWithdrawalData([]);
    }
  };

  const handleManualPaymentToggle = () => {
    setIsManualPayment(true);
  };

  const handleOnlinePaymentToggle = () => {
    setIsManualPayment(false);
  };

  const handleAcceptPayment = async (transactionId: number, utrNo: string, uid: string, amount: number, coin: string) => {
    // Call the API to approve the withdrawal
    await approveOrRejectManualWithdrawal(utrNo, uid, coin, amount, true);
    console.log(`Accepted withdrawal for transaction ID: ${transactionId}`);
  };

  const handleRejectPayment = async (transactionId: number, utrNo: string, uid: string, amount: number, coin: string) => {
    // Call the API to reject the withdrawal
    await approveOrRejectManualWithdrawal(utrNo, uid, coin, amount, false);
    console.log(`Rejected withdrawal for transaction ID: ${transactionId}`);
  };

  const approveOrRejectManualWithdrawal = async (utr_no: string, uid: string, coin: string, amount: number, isApproved: boolean) => {
    try {
      await axios.post(`${baseURL}/approveRejectManualWithdrawal`, {
        utr_no,
        uid,
        coin,
        amount,
        isApproved,
      });
      // Optionally refresh withdrawal data after processing
      fetchWithdrawalData();
    } catch (error) {
      console.error('Error processing manual withdrawal:', error);
    }
  };

  return (
    <div className="ml-0 lg:ml-[256px]">
      <div className="flex gap-4 mt-4">
        <button
          className={`px-4 py-2 rounded ${isManualPayment ? 'bg-blue-500 text-white' : 'bg-gray-300'}`}
          onClick={handleManualPaymentToggle}
        >
          Manual Withdrawal
        </button>
        <button
          className={`px-4 py-2 rounded ${!isManualPayment ? 'bg-blue-500 text-white' : 'bg-gray-300'}`}
          onClick={handleOnlinePaymentToggle}
        >
          Online Withdrawal
        </button>
      </div>

      {/* Conditional Table */}
      <div className="mt-6">
        {isManualPayment ? (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-white">Manual Withdrawal Details</h2>
            <table className="min-w-full bg-white bg-opacity-5 text-white">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">SNo.</th>
                  <th className="px-4 py-2 border">User Name</th>
                  <th className="px-4 py-2 border">Amount</th>
                  <th className="px-4 py-2 border">UTR No</th>
                  <th className="px-4 py-2 border">Status</th>
                  <th className="px-4 py-2 border">Date</th>
                  <th className="px-4 py-2 border">Actions</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {withdrawalData.map((withdrawal, index) => (
                  <tr key={withdrawal.transaction_id}>
                    <td className="px-4 py-2 border">{index + 1}</td>
                    <td className="px-4 py-2 border">{withdrawal.name}</td>
                    <td className="px-4 py-2 border">₹{withdrawal.amount.toFixed(1)}</td>
                    <td className="px-4 py-2 border">{withdrawal.utr_no}</td>
                    <td className="px-4 py-2 border">{withdrawal.status}</td>
                    <td className="px-4 py-2 border">{new Date(withdrawal.transaction_date).toLocaleString()}</td>
                    <td className="px-4 py-2 border">
                      {withdrawal.status === 'pending' ? (
                        <>
                          <button
                            className="px-3 py-1 mr-2 bg-green-500 text-white rounded"
                            onClick={() => handleAcceptPayment(withdrawal.transaction_id, withdrawal.utr_no, withdrawal.uid, withdrawal.amount, 'inr')}
                          >
                            Accept
                          </button>
                          <button
                            className="px-3 py-1 bg-red-500 text-white rounded"
                            onClick={() => handleRejectPayment(withdrawal.transaction_id, withdrawal.utr_no, withdrawal.uid, withdrawal.amount, 'inr')}
                          >
                            Reject
                          </button>
                        </>
                      ) : withdrawal.status === 'accepted' ? (
                        <button className="px-3 py-1 mr-2 bg-green-500 text-white rounded">Accepted</button>
                      ) : (
                        <button className="px-3 py-1 mr-2 bg-red-500 text-white rounded">Rejected</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-white">Online Withdrawal Details</h2>
			<table className="min-w-full bg-white bg-opacity-5 text-white">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">SNo.</th>
                  <th className="px-4 py-2 border">User Name</th>
                  <th className="px-4 py-2 border">Amount</th>
                  <th className="px-4 py-2 border">UTR No</th>
                  <th className="px-4 py-2 border">Status</th>
                  <th className="px-4 py-2 border">Date</th>
                  <th className="px-4 py-2 border">Actions</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {withdrawalData.map((withdrawal, index) => (
                  <tr key={withdrawal.transaction_id}>
                    <td className="px-4 py-2 border">{index + 1}</td>
                    <td className="px-4 py-2 border">{withdrawal.name}</td>
                    <td className="px-4 py-2 border">₹{withdrawal.amount.toFixed(1)}</td>
                    <td className="px-4 py-2 border">{withdrawal.utr_no}</td>
                    <td className="px-4 py-2 border">{withdrawal.status}</td>
                    <td className="px-4 py-2 border">{new Date(withdrawal.transaction_date).toLocaleString()}</td>
                    <td className="px-4 py-2 border">
                      {withdrawal.status === 'pending' ? (
                        <>
                          <button
                            className="px-3 py-1 mr-2 bg-green-500 text-white rounded"
                            onClick={() => handleAcceptPayment(withdrawal.transaction_id, withdrawal.utr_no, withdrawal.uid, withdrawal.amount, 'inr')}
                          >
                            Accept
                          </button>
                          <button
                            className="px-3 py-1 bg-red-500 text-white rounded"
                            onClick={() => handleRejectPayment(withdrawal.transaction_id, withdrawal.utr_no, withdrawal.uid, withdrawal.amount, 'inr')}
                          >
                            Reject
                          </button>
                        </>
                      ) : withdrawal.status === 'accepted' ? (
                        <button className="px-3 py-1 mr-2 bg-green-500 text-white rounded">Accepted</button>
                      ) : (
                        <button className="px-3 py-1 mr-2 bg-red-500 text-white rounded">Rejected</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualWithdrawal;
